import React, { useState, useEffect, useCallback } from 'react';
import { FiArchive, FiAlertCircle, FiFileText, FiRefreshCw, FiDownload, FiFilter, FiPackage, FiTrash2, FiCheckCircle } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8em;
  padding: 3em 1em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;

  svg { font-size: 2.5em; }
  p { font-size: 0.9em; text-align: center; }
`;

const ProgressBarWrap = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
  border-radius: 3px;
  overflow: hidden;
  margin-top: 0.6em;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background: ${({ $done, theme }) => $done
      ? '#10b981'
      : `linear-gradient(90deg, ${theme.primary}aa, ${theme.primary}, ${theme.primary}aa)`};
    background-size: ${({ $done }) => $done ? 'auto' : '200% 100%'};
    animation: ${({ $done }) => $done ? 'none' : shimmer} 1.4s linear infinite;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

const ProgressLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.3em;
  font-size: 0.72em;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1em;

  span { font-size: 0.85em; font-weight: 600; color: ${({ theme }) => theme.text}; opacity: 0.7; }

  button {
    display: flex; align-items: center; gap: 0.4em;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'};
    border-radius: 6px; padding: 0.4em 0.8em; font-size: 0.8em;
    color: ${({ theme }) => theme.text}; cursor: pointer; transition: all 0.2s;
    svg { font-size: 0.9em; }
    &:hover { border-color: ${({ theme }) => theme.primary}; color: ${({ theme }) => theme.primary}; }
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'};
  border: 1px solid ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
  border-radius: 12px; padding: 1em 1.2em; margin-bottom: 0.8em; transition: border-color 0.2s;
  &:hover { border-color: ${({ theme }) => theme.primary}; }
`;

const CardHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.7em;
`;

const CardTitle = styled.div`
  display: flex; align-items: center; gap: 0.5em;
  font-size: 0.85em; font-weight: 600; color: ${({ theme }) => theme.text};
  svg { color: ${({ theme }) => theme.primary}; font-size: 1em; }
`;

const CardDate = styled.span`
  font-size: 0.75em; color: ${({ theme }) => theme.text}; opacity: 0.5;
`;

const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5em; margin-bottom: 0.8em;
`;

const Stat = styled.div`
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)'};
  border-radius: 6px; padding: 0.5em 0.6em; text-align: center;
  .label { font-size: 0.65em; opacity: 0.5; text-transform: uppercase; letter-spacing: 0.4px; color: ${({ theme }) => theme.text}; }
  .value { font-size: 1em; font-weight: 700; }
`;

const FiltersRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.4em; margin-bottom: 0.8em;
`;

const FilterChip = styled.span`
  font-size: 0.72em; padding: 0.2em 0.6em; border-radius: 6px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.12)'};
  color: ${({ theme }) => theme.primary};
  border: 1px solid ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.25)'};
`;

const ButtonsRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 0.5em; margin-top: 0.2em;
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 0.4em;
  padding: 0.55em 1em; font-size: 0.8em; font-weight: 500;
  border-radius: 6px; cursor: pointer; transition: all 0.2s;
  justify-content: center; white-space: nowrap;
  background: ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.primary :
    $variant === 'danger' ? 'rgba(239,68,68,0.08)' :
    (theme.body === '#fafbfc' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)')};
  color: ${({ $variant, theme }) =>
    $variant === 'primary' ? '#fff' :
    $variant === 'danger' ? '#ef4444' : theme.text};
  border: 1px solid ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.primary :
    $variant === 'danger' ? 'rgba(239,68,68,0.3)' :
    (theme.body === '#fafbfc' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)')};
  svg { font-size: 0.95em; }
  &:hover { opacity: 0.85; transform: translateY(-1px); }
  &:disabled { opacity: 0.3; cursor: not-allowed; transform: none; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const buildFilterChips = (f) => {
  if (!f) return [];
  const chips = [];
  if (f.dataInicio || f.dataFim) chips.push(`${f.dataInicio || '...'} → ${f.dataFim || '...'}`);
  if (f.modelo?.length) chips.push(...f.modelo.map(m => m === '55' ? 'NF-e' : m === '57' ? 'CT-e' : m));
  if (f.cfop?.length) chips.push(...f.cfop.map(c => `CFOP ${c}`));
  if (f.direcao) chips.push(f.direcao === 'propria' ? 'Emissão Própria' : 'Terceiros');
  if (f.status) chips.push(f.status);
  return chips;
};

// ─── Componente ───────────────────────────────────────────────────────────────

export default function XmlDownloadHistoryTab() {
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadState, setDownloadState] = useState({}); // { [outputId]: { progress, done, error, rate } }
  const [deleting, setDeleting] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null); // outputId pendente

  const loadOutputs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/xml-download/filter-outputs');
      setOutputs(res.data.outputs || []);
    } catch {
      toast.error('Erro ao carregar histórico de downloads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOutputs(); }, [loadOutputs]);

  const downloadZip = async (outputId) => {
    setDownloadState(s => ({ ...s, [outputId]: { progress: 0, done: false, error: false, rate: 0 } }));
    let lastLoaded = 0;
    let lastTime = Date.now();
    try {
      const response = await api.get(
        `/xml-download/download-filter-zip?outputId=${outputId}`,
        {
          responseType: 'blob',
          timeout: 0,
          onDownloadProgress: (e) => {
            const now = Date.now();
            const elapsed = (now - lastTime) / 1000;
            let rate = 0;
            if (elapsed >= 0.2) {
              rate = (e.loaded - lastLoaded) / elapsed / (1024 * 1024);
              lastLoaded = e.loaded;
              lastTime = now;
            }
            const progress = e.total > 0 ? Math.round((e.loaded / e.total) * 100) : Math.min(99, Math.round(e.loaded / (1024 * 1024)));
            setDownloadState(s => ({ ...s, [outputId]: { progress, done: false, error: false, rate: parseFloat(rate.toFixed(1)) } }));
          },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `xmls_${outputId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloadState(s => ({ ...s, [outputId]: { progress: 100, done: true, error: false, rate: 0 } }));
      toast.success('Download concluído!');
      setTimeout(() => setDownloadState(s => { const n = { ...s }; delete n[outputId]; return n; }), 4000);
    } catch {
      setDownloadState(s => ({ ...s, [outputId]: { progress: 0, done: false, error: true, rate: 0 } }));
      toast.error('Erro ao baixar arquivo');
      setTimeout(() => setDownloadState(s => { const n = { ...s }; delete n[outputId]; return n; }), 3000);
    }
  };

  const deleteOutput = async (outputId) => {
    setDeleting(d => ({ ...d, [outputId]: true }));
    try {
      await api.delete(`/xml-download/filter-output/${outputId}`);
      setOutputs(o => o.filter(x => x.outputId !== outputId));
      toast.success('Download deletado.');
      setConfirmDelete(null);
    } catch {
      toast.error('Erro ao deletar');
    } finally {
      setDeleting(d => ({ ...d, [outputId]: false }));
    }
  };

  if (loading) {
    return (
      <EmptyState>
        <FiRefreshCw style={{ animation: 'spin 1s linear infinite' }} />
        <p>Carregando histórico...</p>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </EmptyState>
    );
  }

  return (
    <>
      <ConfirmDeleteModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteOutput(confirmDelete)}
        title="Deletar download?"
        description="Todos os arquivos deste processamento serão removidos permanentemente. Esta ação não pode ser desfeita."
        confirmLabel="Deletar"
        loading={!!deleting[confirmDelete]}
      />

      <Header>
        <span>{outputs.length} solicitaç{outputs.length !== 1 ? 'ões' : 'ão'} encontrada{outputs.length !== 1 ? 's' : ''}</span>
        <button onClick={loadOutputs}><FiRefreshCw /> Atualizar</button>
      </Header>

      {outputs.length === 0 ? (
        <EmptyState>
          <FiPackage />
          <p>Nenhum download por filtros encontrado.<br />Use a aba Avançado para gerar novos.</p>
        </EmptyState>
      ) : outputs.map((o) => {
        const chips = buildFilterChips(o.filtrosAplicados);
        const stats = o.estatisticas || {};
        const dl = downloadState[o.outputId];
        const isDeleting = deleting[o.outputId];

        return (
          <Card key={o.outputId}>
            <CardHeader>
              <CardTitle>
                <FiArchive />
                {formatDate(o.geradoEm)}
              </CardTitle>
              <CardDate>{formatBytes(stats.tamanhoZipBytes)}</CardDate>
            </CardHeader>

            {chips.length > 0 && (
              <FiltersRow>
                <FiFilter style={{ fontSize: '0.8em', opacity: 0.4, marginTop: '0.2em' }} />
                {chips.map((c, i) => <FilterChip key={i}>{c}</FilterChip>)}
              </FiltersRow>
            )}

            <StatsRow>
              <Stat>
                <div className="label">Total</div>
                <div className="value">{(stats.totalFiltradas || 0).toLocaleString('pt-BR')}</div>
              </Stat>
              <Stat>
                <div className="label">Encontrados</div>
                <div className="value" style={{ color: '#10b981' }}>{(stats.encontradasEmDisco || 0).toLocaleString('pt-BR')}</div>
              </Stat>
              <Stat>
                <div className="label">Faltantes</div>
                <div className="value" style={{ color: stats.faltantesEmDisco > 0 ? '#ef4444' : 'inherit' }}>
                  {(stats.faltantesEmDisco || 0).toLocaleString('pt-BR')}
                </div>
              </Stat>
            </StatsRow>

            {dl && (
              <>
                <ProgressBarWrap $progress={dl.progress} $done={dl.done} />
                <ProgressLabel>
                  <span>
                    {dl.done ? 'Download concluído!' : dl.error ? 'Erro no download' : (
                      dl.rate > 0 ? `Baixando... ${dl.rate} MB/s` : 'Aguardando...'
                    )}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.3em' }}>
                    {dl.done
                      ? <FiCheckCircle style={{ color: '#10b981' }} />
                      : <strong>{dl.progress}%</strong>
                    }
                  </span>
                </ProgressLabel>
              </>
            )}

            <ButtonsRow style={{ marginTop: dl ? '0.6em' : '0.2em' }}>
              <Btn
                $variant="primary"
                disabled={!o.hasZip || !!dl}
                onClick={() => downloadZip(o.outputId)}
              >
                <FiDownload />
                {dl && !dl.done && !dl.error ? `${dl.progress}%` : 'Baixar Tudo'}
              </Btn>
              <Btn
                $variant="danger"
                disabled={isDeleting || !!dl}
                onClick={() => setConfirmDelete(o.outputId)}
                style={{ padding: '0.55em 0.8em' }}
              >
                <FiTrash2 />
              </Btn>
            </ButtonsRow>
          </Card>
        );
      })}
    </>
  );
}
